'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  Bot,
  Clock,
  Monitor,
  Package,
  Plus,
  Truck,
  Wrench,
} from 'lucide-react';

import { useTranslation } from '@/hooks/useTranslation';
import { colors, typography } from '@/styles/theme';
import { Skeleton } from '@/components/ui/skeleton';
import {
  reportApi,
  robotApi,
  stageDepartment,
  type InventoryReport,
  type RobotLifecycleStage,
  type RobotUnit,
  type StageDepartment,
} from '../_lib/api';

const DEPT_COLORS: Record<StageDepartment, { bg: string; text: string }> = {
  SUPPLY: { bg: '#e8f3ff', text: '#3370ff' },
  LOGISTICS: { bg: '#fff7e8', text: '#ff7d00' },
  WAREHOUSE: { bg: '#f0e8ff', text: '#7b61ff' },
  SALES: { bg: '#fff8e6', text: '#d4a017' },
  DELIVERY: { bg: '#e6fff0', text: '#0fc66e' },
  RENTAL: { bg: '#cffae6', text: '#00866b' },
  AFTERSALES: { bg: '#ffece8', text: '#f53f3f' },
  TERMINAL: { bg: '#f7f8fa', text: '#8f959e' },
};

function bucketByDepartment(byStage: Partial<Record<string, number>> = {}) {
  const buckets = {} as Record<StageDepartment, number>;
  for (const [stage, count] of Object.entries(byStage)) {
    const dept = stageDepartment(stage as RobotLifecycleStage);
    buckets[dept] = (buckets[dept] ?? 0) + (count ?? 0);
  }
  return buckets;
}

function StageBadge({ stage, label }: { stage: RobotLifecycleStage; label: string }) {
  const c = DEPT_COLORS[stageDepartment(stage)];
  return (
    <span className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: c.bg, color: c.text }}>
      {label}
    </span>
  );
}

export default function RobotDashboardPage() {
  const { t, locale } = useTranslation();
  const rm = t.robotManager;

  const [inventory, setInventory] = useState<InventoryReport | null>(null);
  const [recent, setRecent] = useState<RobotUnit[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      reportApi.inventory().catch(() => null),
      robotApi.list({ page: 1, limit: 8, sortBy: 'updatedAt', sortOrder: 'desc' }).catch(() => null),
    ])
      .then(([inv, list]) => {
        setInventory(inv);
        setRecent(list?.items ?? []);
      })
      .finally(() => setLoading(false));
  }, []);

  const deptBuckets = useMemo(
    () => bucketByDepartment(inventory?.byStage ?? {}),
    [inventory?.byStage],
  );

  const cards = [
    {
      label: rm.dashboard.totalDevices,
      value: inventory?.total ?? 0,
      icon: Package,
      color: colors.primary,
      bg: '#e8f3ff',
      href: '/robot-manager',
    },
    {
      label: rm.dashboard.inStock,
      value: inventory?.available ?? 0,
      icon: Package,
      color: colors.success,
      bg: '#e8ffea',
      href: '/robot-manager?status=WAREHOUSE_BRANDED_READY',
    },
    {
      label: rm.dashboard.pendingDelivery,
      value: (deptBuckets.SALES ?? 0) + (deptBuckets.DELIVERY ?? 0),
      icon: Truck,
      color: colors.warning,
      bg: '#fff7e8',
      href: '/robot-manager?status=DELIVERY_READY',
    },
    {
      label: rm.dashboard.inRepair,
      value: deptBuckets.AFTERSALES ?? 0,
      icon: Wrench,
      color: colors.error,
      bg: '#ffece8',
      href: '/robot-manager?status=AFTERSALES_UNDER_REPAIR',
    },
  ];

  const quickLinks = [
    { icon: Plus, label: rm.help.tasks.create, href: '/robot-manager/create' },
    { icon: Monitor, label: rm.help.tasks.list, href: '/robot-manager' },
    { icon: BarChart3, label: rm.help.tasks.reports, href: '/robot-manager/reports' },
  ];

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: colors.bgSecondary }}>
      <div className="bg-white border-b" style={{ borderColor: colors.border }}>
        <div className="px-6 h-12 flex items-center">
          <h1 className={typography.h4}>{rm.dashboard.title}</h1>
          <span className={`${typography.caption} ml-3`}>{rm.dashboard.subtitle}</span>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
          {/* 指标卡片 */}
          <section>
            <h2 className={`${typography.h4} mb-3`}>{rm.dashboard.kpi}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-28 rounded-lg" />
                ))
              ) : (
                cards.map((c) => {
                  const Icon = c.icon;
                  return (
                    <Link
                      key={c.label}
                      href={c.href}
                      className="rounded-lg p-5 transition-all"
                      style={{
                        backgroundColor: colors.bgPrimary,
                        border: `1px solid ${colors.border}`,
                      }}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: c.bg }}>
                          <Icon className="w-5 h-5" style={{ color: c.color }} />
                        </div>
                        <span className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                          {c.label}
                        </span>
                      </div>
                      <div className="text-2xl font-semibold tabular-nums" style={{ color: colors.textPrimary }}>
                        {c.value}
                      </div>
                    </Link>
                  );
                })
              )}
            </div>
          </section>

          {/* 快捷入口 */}
          <section>
            <h2 className={`${typography.h4} mb-3`}>{rm.dashboard.quickActions}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {quickLinks.map((q) => {
                const Icon = q.icon;
                return (
                  <Link
                    key={q.label}
                    href={q.href}
                    className="rounded-lg p-4 flex flex-col items-center gap-2 transition-colors"
                    style={{
                      backgroundColor: colors.bgPrimary,
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <div
                      className="w-10 h-10 rounded flex items-center justify-center"
                      style={{ backgroundColor: `${colors.primary}12`, color: colors.primary }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-sm font-medium text-center">{q.label}</span>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* 近期更新 */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className={typography.h4}>{rm.dashboard.recentUpdates}</h2>
              <Link href="/robot-manager" className={typography.link}>
                {rm.dashboard.viewAll} <ArrowRight className="w-3 h-3 inline" />
              </Link>
            </div>
            <div
              className="rounded-lg overflow-hidden"
              style={{ backgroundColor: colors.bgPrimary, border: `1px solid ${colors.border}` }}
            >
              {loading ? (
                <div className="p-6 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : recent.length === 0 ? (
                <div className="p-8 text-center text-sm" style={{ color: colors.textTertiary }}>
                  {rm.messages.noDataMessage}
                </div>
              ) : (
                <ul>
                  {recent.map((r) => (
                    <li
                      key={r.id}
                      className="px-5 py-3 flex items-center justify-between"
                      style={{ borderBottom: `1px solid ${colors.border}` }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Bot className="w-4 h-4 shrink-0" style={{ color: colors.textTertiary }} />
                        <Link
                          href={`/robot-manager/${r.id}`}
                          className={`${typography.link} truncate`}
                        >
                          {r.ffsn}
                        </Link>
                        <span className={`${typography.caption} truncate`}>
                          {r.model?.name ?? '-'} / {r.sku?.code ?? '-'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <StageBadge
                          stage={(r.snapshot?.currentStage ?? 'CLOSED') as RobotLifecycleStage}
                          label={(rm.status as any)[r.snapshot?.currentStage ?? 'CLOSED'] ?? r.snapshot?.currentStage ?? 'CLOSED'}
                        />
                        <span className={typography.caption}>
                          <Clock className="w-3 h-3 inline mr-1" />
                          {r.updatedAt
                            ? new Date(r.updatedAt).toLocaleString(
                                locale === 'en' ? 'en-US' : 'zh-CN',
                              )
                            : ''}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
